import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Users,
  Clock,
  DollarSign,
  Search,
  Edit,
  Trash2,
  Check,
  CreditCard,
  Plus,
  X,
  Building,
  User,
  Grid3X3,
  List as ListIcon,
  Eye,
  Activity,
} from 'lucide-react';
import { Dropdown, DatePicker } from '../forms';
import LoadingSpinner from '../LoadingSpinner';
import ManpowerLogForm from './ManpowerLogForm';
import {
  ManpowerLogEntry,
  ManpowerLogQuery,
  ManpowerLogStats,
  CreateManpowerLogData,
  Project,
} from '../../types';
import manpowerLogService from '../../services/manpowerLogService';

interface ManpowerLogManagementProps {
  projectId?: string;
  projects: Project[];
  showProjectSelector?: boolean;
}

const ManpowerLogManagement: React.FC<ManpowerLogManagementProps> = ({
  projectId: initialProjectId,
  projects,
  showProjectSelector = false,
}) => {
  // State Management
  const [entries, setEntries] = useState<ManpowerLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ManpowerLogStats | null>(null);

  // Project Selection
  const [selectedProject, setSelectedProject] = useState(initialProjectId || '');

  // View and Selection
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
  const [_currentPage, setCurrentPage] = useState(1);
  const [_totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);

  // Modals and Actions
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [_showDetailsModal, setShowDetailsModal] = useState(false);
  const [_showPayrollModal, _setShowPayrollModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<ManpowerLogEntry | null>(null);
  const [_viewingEntry, setViewingEntry] = useState<ManpowerLogEntry | null>(null);
  const [submittingEntry, setSubmittingEntry] = useState(false);

  // Filters and Search
  const [query, setQuery] = useState<ManpowerLogQuery>({
    page: 1,
    limit: 20,
    sort_by: 'logged_at',
    sort_order: 'desc',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  // Dropdown options
  const [statusOptions, setStatusOptions] = useState<any[]>([]);
  const [roleOptions, setRoleOptions] = useState<any[]>([]);

  const sortOptions = [
    { value: 'logged_at', label: 'Date Logged' },
    { value: 'date', label: 'Work Date' },
    { value: 'hours_worked', label: 'Hours Worked' },
    { value: 'total_payment', label: 'Total Payment' },
    { value: 'status', label: 'Status' },
  ];

  // Load initial data
  useEffect(() => {
    loadDropdownOptions();
  }, []);

  // Load data when project or query changes
  useEffect(() => {
    if (selectedProject) {
      loadData();
      loadStats();
    }
  }, [selectedProject, query]);

  // Auto-select first project if only one available
  useEffect(() => {
    if (!selectedProject && projects.length === 1) {
      setSelectedProject(projects[0].id);
    }
  }, [projects, selectedProject]);

  const loadDropdownOptions = async () => {
    try {
      const [statusData, roleData] = await Promise.all([
        manpowerLogService.getStatusOptions(),
        manpowerLogService.getWorkerRoles(),
      ]);

      setStatusOptions([{ value: '', label: 'All Status' }, ...statusData]);

      setRoleOptions([{ value: '', label: 'All Roles' }, ...roleData]);
    } catch (error) {
      console.error('Failed to load dropdown options:', error);
    }
  };

  const loadData = async () => {
    if (!selectedProject) return;

    setLoading(true);
    try {
      const result = await manpowerLogService.getManpowerLogs({
        ...query,
        project_id: selectedProject,
      });

      if (result.success && result.data) {
        setEntries(result.data.entries || []);
        setTotalEntries(result.data.total || 0);
        setTotalPages(result.data.totalPages || 1);
      } else {
        setEntries([]);
        setTotalEntries(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Failed to load manpower logs:', error);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!selectedProject) return;

    try {
      const dateFilter =
        dateRange.from && dateRange.to
          ? {
              from: dateRange.from,
              to: dateRange.to,
            }
          : undefined;

      const result = await manpowerLogService.getManpowerStats(selectedProject, dateFilter);
      if (result.success && result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  // Update query when filters change
  useEffect(() => {
    const newQuery: ManpowerLogQuery = {
      ...query,
      project_id: selectedProject || undefined,
      status: selectedStatus || undefined,
      role: selectedRole || undefined,
      search: searchTerm || undefined,
      date_from: dateRange.from || undefined,
      date_to: dateRange.to || undefined,
      page: 1,
    };
    setQuery(newQuery);
    setCurrentPage(1);
  }, [selectedProject, selectedStatus, selectedRole, searchTerm, dateRange]);

  // Selection handlers
  const toggleEntrySelection = (entryId: string) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      newSelected.add(entryId);
    }
    setSelectedEntries(newSelected);
  };

  const selectAllEntries = () => {
    setSelectedEntries(new Set(entries.map(e => e.entry_id)));
  };

  const clearSelection = () => {
    setSelectedEntries(new Set());
  };

  // CRUD operations
  const handleCreateEntry = async (formData: CreateManpowerLogData) => {
    setSubmittingEntry(true);
    try {
      const result = await manpowerLogService.createManpowerLog(formData);
      if (result.success) {
        setShowAddForm(false);
        loadData();
        loadStats();
      } else {
        console.error('Failed to create entry:', result.message);
      }
    } catch (error) {
      console.error('Failed to create manpower entry:', error);
    } finally {
      setSubmittingEntry(false);
    }
  };

  const handleUpdateEntry = async (formData: CreateManpowerLogData) => {
    if (!editingEntry) return;

    setSubmittingEntry(true);
    try {
      const result = await manpowerLogService.updateManpowerLog(editingEntry.entry_id, formData);
      if (result.success) {
        setShowEditForm(false);
        setEditingEntry(null);
        loadData();
        loadStats();
      } else {
        console.error('Failed to update entry:', result.message);
      }
    } catch (error) {
      console.error('Failed to update manpower entry:', error);
    } finally {
      setSubmittingEntry(false);
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (!confirm('Are you sure you want to delete this entry?')) return;

    try {
      const result = await manpowerLogService.deleteManpowerLog(entryId);
      if (result.success) {
        loadData();
        loadStats();
      } else {
        console.error('Failed to delete entry:', result.message);
      }
    } catch (error) {
      console.error('Failed to delete entry:', error);
    }
  };

  // Bulk operations
  const handleBulkAction = async (action: 'approved' | 'paid' | 'cancelled', notes?: string) => {
    if (selectedEntries.size === 0) return;

    try {
      const result = await manpowerLogService.bulkUpdateStatus(
        Array.from(selectedEntries),
        action,
        notes
      );
      if (result.success) {
        clearSelection();
        loadData();
        loadStats();
      }
    } catch (error) {
      console.error(`Bulk ${action} failed:`, error);
    }
  };

  // Individual entry actions
  const handleApprove = async (entryId: string) => {
    try {
      await manpowerLogService.bulkUpdateStatus([entryId], 'approved');
      loadData();
      loadStats();
    } catch (error) {
      console.error('Approve failed:', error);
    }
  };

  const handleMarkAsPaid = async (entryId: string, notes?: string) => {
    try {
      await manpowerLogService.bulkUpdateStatus([entryId], 'paid', notes);
      loadData();
      loadStats();
    } catch (error) {
      console.error('Mark as paid failed:', error);
    }
  };

  const handleEditEntry = (entry: ManpowerLogEntry) => {
    setEditingEntry(entry);
    setShowEditForm(true);
  };

  const handleViewEntry = (entry: ManpowerLogEntry) => {
    setViewingEntry(entry);
    setShowDetailsModal(true);
  };

  // Utility functions
  const formatCurrency = (amount: number) => manpowerLogService.formatCurrency(amount);
  const formatHours = (hours: number) => manpowerLogService.formatHours(hours);
  const getStatusColor = (status: string) => manpowerLogService.getStatusColor(status);

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(dateString));
  };

  const _getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || projectId;
  };

  // Project selection required
  if (!selectedProject && projects.length > 0) {
    return (
      <div className="text-center py-12">
        <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Project</h3>
        <p className="text-gray-600 mb-6">Please select a project to view manpower logs.</p>
        <div className="max-w-md mx-auto">
          <Dropdown
            label=""
            placeholder="Select a project"
            options={projects.map(p => ({ value: p.id, label: p.name }))}
            value={selectedProject}
            onChange={setSelectedProject}
          />
        </div>
      </div>
    );
  }

  if (loading && !entries.length) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Manpower Logs</h2>
            <p className="text-sm text-gray-600">
              Track individual worker and company team hours with detailed logging
            </p>
          </div>

          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Log Hours
          </button>
        </div>

        {/* Project Selection */}
        {showProjectSelector && projects.length > 1 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Project Selection</h3>
                <p className="text-sm text-gray-600">Choose a project to view manpower logs</p>
              </div>
              <div className="w-80">
                <Dropdown
                  label=""
                  placeholder="Select a project"
                  options={projects.map(p => ({ value: p.id, label: p.name }))}
                  value={selectedProject}
                  onChange={setSelectedProject}
                />
              </div>
            </div>
          </div>
        )}

        {/* Stats Dashboard */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <div className="bg-green-100 p-2 rounded-lg">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Total Payment</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(stats.totalPayment || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <div className="bg-yellow-100 p-2 rounded-lg">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Total Hours</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatHours(stats.totalHours || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <div className="bg-brand-100 p-2 rounded-lg">
                  <Users className="h-5 w-5 text-brand-primary" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Total Workers</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalWorkers || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <Activity className="h-5 w-5 text-purple-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Entries</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalEntries || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search entries..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <Dropdown
              label=""
              placeholder="All Status"
              options={statusOptions}
              value={selectedStatus}
              onChange={setSelectedStatus}
            />

            <Dropdown
              label=""
              placeholder="All Roles"
              options={roleOptions}
              value={selectedRole}
              onChange={setSelectedRole}
            />

            <DatePicker
              placeholder="From Date"
              value={dateRange.from}
              onChange={date => setDateRange(prev => ({ ...prev, from: date || '' }))}
            />

            <DatePicker
              placeholder="To Date"
              value={dateRange.to}
              onChange={date => setDateRange(prev => ({ ...prev, to: date || '' }))}
            />

            <Dropdown
              label=""
              placeholder="Sort by Date"
              options={sortOptions}
              value={query.sort_by || 'logged_at'}
              onChange={value => setQuery(prev => ({ ...prev, sort_by: value as any }))}
            />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {totalEntries} entr{totalEntries !== 1 ? 'ies' : 'y'} found
            </span>

            {/* View Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white text-red-500 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="List View"
              >
                <ListIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-white text-red-500 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Grid View"
              >
                <Grid3X3 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedEntries.size > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-red-800 font-medium">
                {selectedEntries.size} entr{selectedEntries.size !== 1 ? 'ies' : 'y'} selected
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleBulkAction('approved')}
                  className="px-3 py-1 text-sm text-brand-700 hover:bg-brand-100 rounded-md transition-colors"
                >
                  Approve All
                </button>
                <button
                  onClick={() => handleBulkAction('paid')}
                  className="px-3 py-1 text-sm text-green-700 hover:bg-green-100 rounded-md transition-colors"
                >
                  Mark as Paid
                </button>
                <button
                  onClick={clearSelection}
                  className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Entries Display */}
        <div className="space-y-6">
          {/* List View */}
          {viewMode === 'list' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={selectedEntries.size === entries.length && entries.length > 0}
                          onChange={e => {
                            if (e.target.checked) {
                              selectAllEntries();
                            } else {
                              clearSelection();
                            }
                          }}
                          className="rounded border-gray-300 text-red-500 focus:ring-red-500"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Worker/Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role & Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Hours & Payment
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {entries.map(entry => (
                      <tr key={entry.entry_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedEntries.has(entry.entry_id)}
                            onChange={() => toggleEntrySelection(entry.entry_id)}
                            className="rounded border-gray-300 text-red-500 focus:ring-red-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div
                                className={`h-10 w-10 rounded-full flex items-center justify-center ${
                                  entry.worker_id ? 'bg-brand-100' : 'bg-purple-100'
                                }`}
                              >
                                {entry.worker_id ? (
                                  <User className="h-5 w-5 text-brand-primary" />
                                ) : (
                                  <Building className="h-5 w-5 text-purple-600" />
                                )}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {entry.worker_name || entry.company_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {entry.number_of_workers > 1
                                  ? `${entry.number_of_workers} workers`
                                  : '1 worker'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{entry.role}</div>
                          <div className="text-sm text-gray-500">{entry.location.site}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatHours(entry.hours_worked)}
                            {entry.overtime_hours > 0 && (
                              <span className="text-red-500">
                                {' '}
                                +{formatHours(entry.overtime_hours)} OT
                              </span>
                            )}
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(entry.total_payment)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(entry.status)}`}
                          >
                            {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(entry.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewEntry(entry)}
                              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEditEntry(entry)}
                              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            {entry.status === 'pending' && (
                              <button
                                onClick={() => handleApprove(entry.entry_id)}
                                className="p-1 text-brand-primary hover:text-brand-800 hover:bg-brand-100 rounded transition-colors"
                                title="Approve"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                            )}
                            {entry.status === 'approved' && (
                              <button
                                onClick={() => handleMarkAsPaid(entry.entry_id)}
                                className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors"
                                title="Mark as Paid"
                              >
                                <CreditCard className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteEntry(entry.entry_id)}
                              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {entries.map(entry => (
                <div
                  key={entry.entry_id}
                  className={`bg-white rounded-xl shadow-sm border transition-all hover:shadow-md ${
                    selectedEntries.has(entry.entry_id)
                      ? 'ring-2 ring-red-500 border-red-200'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedEntries.has(entry.entry_id)}
                          onChange={() => toggleEntrySelection(entry.entry_id)}
                          className="rounded border-gray-300 text-red-500 focus:ring-red-500 mr-3"
                        />
                        <div
                          className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                            entry.worker_id ? 'bg-brand-100' : 'bg-purple-100'
                          }`}
                        >
                          {entry.worker_id ? (
                            <User className="h-5 w-5 text-brand-primary" />
                          ) : (
                            <Building className="h-5 w-5 text-purple-600" />
                          )}
                        </div>
                      </div>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(entry.status)}`}
                      >
                        {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                      </span>
                    </div>

                    <div className="mb-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-1">
                        {entry.worker_name || entry.company_name}
                      </h3>
                      <p className="text-sm text-gray-600">{entry.role}</p>
                      <p className="text-sm text-gray-500">{entry.location.site}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-gray-500">Hours</span>
                        <p className="font-medium">
                          {formatHours(entry.hours_worked)}
                          {entry.overtime_hours > 0 && (
                            <span className="text-red-500 text-xs">
                              {' '}
                              +{formatHours(entry.overtime_hours)} OT
                            </span>
                          )}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Payment</span>
                        <p className="font-medium text-gray-900">
                          {formatCurrency(entry.total_payment)}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Workers</span>
                        <p className="font-medium">{entry.number_of_workers}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Date</span>
                        <p className="font-medium">{formatDate(entry.date)}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewEntry(entry)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditEntry(entry)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="flex items-center space-x-2">
                        {entry.status === 'pending' && (
                          <button
                            onClick={() => handleApprove(entry.entry_id)}
                            className="inline-flex items-center px-3 py-1 text-xs font-medium text-brand-700 bg-brand-100 rounded-full hover:bg-brand-200 transition-colors"
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Approve
                          </button>
                        )}
                        {entry.status === 'approved' && (
                          <button
                            onClick={() => handleMarkAsPaid(entry.entry_id)}
                            className="inline-flex items-center px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full hover:bg-green-200 transition-colors"
                          >
                            <CreditCard className="h-3 w-3 mr-1" />
                            Pay
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {entries.length === 0 && selectedProject && (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No manpower logs found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || selectedStatus || selectedRole
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Get started by logging your first manpower entry.'}
              </p>
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Log Hours
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add Entry Drawer */}
      {showAddForm &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
              onClick={() => setShowAddForm(false)}
            />

            {/* Drawer */}
            <div className="fixed top-6 right-6 bottom-6 z-[100] w-[720px] animate-in slide-in-from-right duration-300">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
                {/* Header - Sticky */}
                <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
                  <div className="flex items-center space-x-3">
                    <div className="bg-red-100 p-2 rounded-lg">
                      <Plus className="h-5 w-5 text-red-500" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Log Manpower Hours</h2>
                      <p className="text-sm text-gray-600">
                        Track individual worker or company team hours
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto">
                  <div className="px-6 py-6">
                    <ManpowerLogForm
                      onSubmit={handleCreateEntry}
                      onCancel={() => setShowAddForm(false)}
                      projects={projects}
                      isSubmitting={submittingEntry}
                      initialData={{ project_id: selectedProject }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}

      {/* Edit Entry Drawer */}
      {showEditForm &&
        editingEntry &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
              onClick={() => setShowEditForm(false)}
            />

            {/* Drawer */}
            <div className="fixed top-6 right-6 bottom-6 z-[100] w-[720px] animate-in slide-in-from-right duration-300">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
                {/* Header - Sticky */}
                <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-100 p-2 rounded-lg">
                      <Edit className="h-5 w-5 text-brand-primary" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Edit Manpower Entry</h2>
                      <p className="text-sm text-gray-600">Update worker or company team hours</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowEditForm(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto">
                  <div className="px-6 py-6">
                    <ManpowerLogForm
                      onSubmit={handleUpdateEntry}
                      onCancel={() => setShowEditForm(false)}
                      projects={projects}
                      isSubmitting={submittingEntry}
                      initialData={editingEntry}
                      editMode={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

export default ManpowerLogManagement;
